// DebugPanel.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { format, fromUnixTime } from 'date-fns';
import { debounce, pick, startCase, isEmpty } from 'lodash';

import InputComponent, {
  Theme as InputTheme,
} from '../component/InputComponent.jsx';
import ToggleSwitch from '../component/ToggleSwitch.jsx';
import HighlightSubstring from '../component/HighlightSubstring.jsx';
import DrmPanelTestSection from '../component/DrmPanelTestSection.jsx';
import Loading, { LoadingType } from '../component/Loading.jsx';
import Accordion, { Toggle, Collapse } from '../component/Accordion.jsx';
import { drmRulesetTestStatus } from '../resource/drmConstants.js';
import {
  getUserAgent,
  getIsOnWebviewApp,
  getIsOnAndroid,
} from '../resource/getUserAgent.js';
import {
  initEruda,
  destroyEruda,
  isErudaInitialized,
} from '../resource/mobileConsole.js';

import {
  CONFIG_PRIORITY_CONFIGURE,
  CONFIG_PRIORITY_PRESENCE_CLIENT,
  CONFIG_PRIORITY_PRESENCE_USER,
} from '../resource/configPriority.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import SearchIconSource from '../../img/ic_search_white.svg';
import DropDownIcon from '../../img/ic-dropdown-white-16-x-16.svg';

const {
  ua,
  browser: uaBrowser,
  os: uaOs,
  device: uaDevice,
  engine: uaEngine,
} = getUserAgent();

const isOnWebviewApp = getIsOnWebviewApp();
const isOnAndroid = getIsOnAndroid();

import {
  StyledModal,
  Header,
  BackButton,
  ActionButton,
  MinorActionButton,
  Title,
  Body as SettingModalBody,
  Section,
  SectionBody,
  SectionTitle,
  SectionRow,
  Row,
  RowTitle,
  RowContent,
  TestedAt,
  AccordionRow,
  AccordionSectionRow,
  AccordionRowTitle,
  AccordionDropDownIcon,
  AccordionRowContent,
} from '../style/SettingModal.js';
import { ButtonId } from '../resource/mixpanel.js';
import BackIconSource from '../../img/ic_topbar_back.svg';

const REMOTE_CONFIG_ACCORDION_ID = 'remote-config';

export class DebugPanel extends React.PureComponent {
  state = {
    shouldShowExtraInformation: false,
    remoteConfigKeyword: '',
    isRemoteConfigSearching: false,
    expandChannel: [],
    accordions: {
      'token-accessToken': true,
      'token-refreshToken': true,
      webview: true,
      mdm: true,
    },
    isMobileConsoleEnabled: isErudaInitialized(),
  };
  titleClickTimes = 0;

  componentDidMount() {
    const { refreshToken, handleRefreshToken } = this.props;

    if (refreshToken) {
      handleRefreshToken({ refreshToken });
    }
  }
  componentDidUpdate(prevProps) {
    const {
      isSpeedtested,
      drmTestStatus,
      trackSpeedtestEvent,
      refreshToken,
      handleRefreshToken,
    } = this.props;

    if (refreshToken && refreshToken !== prevProps.refreshToken) {
      handleRefreshToken({ refreshToken });
    }
    if (
      isSpeedtested !== prevProps.isSpeedtested ||
      drmTestStatus !== prevProps.drmTestStatus
    ) {
      if (isSpeedtested && drmTestStatus === drmRulesetTestStatus.TESTED) {
        trackSpeedtestEvent();
      }
    }
  }
  componentWillUnmount() {
    if (this.handleRemoteConfigSearch) {
      this.handleRemoteConfigSearch.cancel();
    }
  }
  handleRemoteConfigSearch = debounce(event => {
    const { currentConfigIds } = this.props;
    const { accordions } = this.state;
    const keyword = event.target.value.replace(/[\b]/g, ' ');
    this.setState({
      remoteConfigKeyword: keyword,
      isRemoteConfigSearching: false,
      accordions: [...currentConfigIds, 'webview'].reduce(
        (accumulated, configId) => ({
          ...accumulated,
          [configId]: !!keyword,
        }),
        {
          ...accordions,
          [REMOTE_CONFIG_ACCORDION_ID]: true,
        }
      ),
    });
  }, 500); // TODO: remote config
  handleCheckButtonOnClick = () => {
    const {
      checkNetworkSpeed,
      recheckDrmCapability,
      speedtestServersUrl,
      fpIdPro,
      getFpIdPro,
    } = this.props;

    checkNetworkSpeed({ speedtestServersUrl });
    recheckDrmCapability();

    if (!fpIdPro) {
      getFpIdPro();
    }
  };
  renderSpeedtestTimestamp = () => {
    const { speedtestUnixTimestamp } = this.props;
    return speedtestUnixTimestamp ? (
      <TestedAt>
        {format(fromUnixTime(speedtestUnixTimestamp), 'yyyy/MM/dd HH:mm:ss')}
      </TestedAt>
    ) : null;
  };
  renderSpeedtestPanel = () => {
    const {
      jitterStatus,
      pingStatus,
      dlProgress,
      ulProgress,
      dlStatus,
      ulStatus,
      isSpeedtesting,
    } = this.props;
    return (
      <>
        <Section key={'Speedtest'}>
          <SectionTitle>
            <div>Speedtest</div>
            {this.renderSpeedtestTimestamp()}
          </SectionTitle>
          <SectionBody>
            <Row>
              <SectionRow>
                <RowTitle>Ping</RowTitle>
                <RowContent>
                  {isSpeedtesting && !pingStatus ? (
                    <Loading />
                  ) : pingStatus ? (
                    `${pingStatus} ms`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
            <Row>
              <SectionRow>
                <RowTitle>Jitter</RowTitle>
                <RowContent>
                  {isSpeedtesting && !jitterStatus ? (
                    <Loading />
                  ) : jitterStatus ? (
                    `${jitterStatus} ms`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
            <Row>
              <SectionRow>
                <RowTitle>
                  Download
                  {dlProgress
                    ? ` (${Number.parseInt(dlProgress * 100)}%)`
                    : null}
                  {dlProgress > 0 && dlProgress < 1 ? (
                    <Loading
                      style={{ display: 'inline-block', marginLeft: '8px' }}
                    />
                  ) : null}
                </RowTitle>
                <RowContent>
                  {isSpeedtesting && !dlStatus ? (
                    <Loading />
                  ) : dlStatus ? (
                    `${dlStatus} Mbit/s`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
            <Row>
              <SectionRow>
                <RowTitle>
                  Upload
                  {ulProgress
                    ? ` (${Number.parseInt(ulProgress * 100)}%)`
                    : null}
                  {ulProgress > 0 && ulProgress < 1 ? (
                    <Loading
                      style={{ display: 'inline-block', marginLeft: '8px' }}
                    />
                  ) : null}
                </RowTitle>
                <RowContent>
                  {isSpeedtesting && !ulStatus ? (
                    <Loading />
                  ) : ulStatus ? (
                    `${ulStatus} Mbit/s`
                  ) : null}
                </RowContent>
              </SectionRow>
            </Row>
          </SectionBody>
        </Section>
      </>
    );
  };
  renderWebViewWebAppConfig = () => {
    if (!isOnWebviewApp) {
      return null;
    }

    const { accordions } = this.state;
    const isActive = !!accordions['webview'];

    return (
      <Section>
        <SectionBody>
          <AccordionRow key="WebView">
            <Accordion
              activeKey={isActive ? 'WebView' : null}
              key="WebView"
              as={AccordionSectionRow}
            >
              <Toggle
                eventKey="WebView"
                as={AccordionRowTitle}
                isActive={isActive}
                onClick={() => {
                  this.setState({
                    accordions: {
                      ...accordions,
                      webview: !isActive,
                    },
                  });
                }}
              >
                <AccordionContent>WebView WebApp Config</AccordionContent>
                <AccordionDropDownIcon src={DropDownIcon} isActive={isActive} />
              </Toggle>
              <Collapse eventKey="WebView" as={AccordionRowContent}>
                <CodeContent>
                  {window.WebApp
                    ? Object.entries(window.WebApp).map(([key, value]) => (
                        <div key={key} style={{ wordBreak: 'break-all' }}>
                          {`${key}: ${typeof value === 'function' ? 'function' : JSON.stringify(value)}`}
                        </div>
                      ))
                    : 'WebApp configuration is not available'}
                </CodeContent>
              </Collapse>
            </Accordion>
          </AccordionRow>
        </SectionBody>
      </Section>
    );
  };

  renderMdmConfig = () => {
    if (typeof window === 'undefined' || !window.__isOnMdm) {
      return null;
    }

    const { accordions } = this.state;
    const isActive = !!accordions['mdm'];

    return (
      <Section>
        <SectionBody>
          <AccordionRow key="mdm">
            <Accordion
              activeKey={isActive ? 'mdm' : null}
              key="mdm"
              as={AccordionSectionRow}
            >
              <Toggle
                eventKey="mdm"
                as={AccordionRowTitle}
                isActive={isActive}
                onClick={() => {
                  this.setState({
                    accordions: {
                      ...accordions,
                      mdm: !isActive,
                    },
                  });
                }}
              >
                <AccordionContent>Mdm Config</AccordionContent>
                <AccordionDropDownIcon src={DropDownIcon} isActive={isActive} />
              </Toggle>
              <Collapse eventKey="mdm" as={AccordionRowContent}>
                <CodeContent>
                  {window.Mdm
                    ? Object.entries(window.Mdm).map(([key, value]) => (
                        <div key={key} style={{ wordBreak: 'break-all' }}>
                          {`${key}: ${typeof value === 'function' ? 'function' : JSON.stringify(value)}`}
                        </div>
                      ))
                    : 'Mdm configuration is not available'}
                </CodeContent>
              </Collapse>
            </Accordion>
          </AccordionRow>
        </SectionBody>
      </Section>
    );
  };

  renderCheckWebviewSettingsButton = () => {
    const { postMessageToApp } = this.props;

    return (
      <MinorActionButton
        onClick={() =>
          postMessageToApp({ message: 'openSystemWebViewSettings' })
        }
      >
        Check Webview Settings
      </MinorActionButton>
    );
  };
  renderDrmPanel = () => {
    const { drmTestAtUnixTimestamp, messageId } = this.props;
    return (
      <DrmPanelTestSection
        drmTestAtUnixTimestamp={drmTestAtUnixTimestamp}
        messageId={messageId}
      />
    );
  };
  renderPusherStatsPanel = () => {
    const { pusherStatsInitSpeed, pusherStatsConnectSpeed } = this.props;
    return (
      <Section key={'PusherStats'}>
        <SectionTitle>Pusher Stats</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Init Speed</RowTitle>
              <RowContent>{pusherStatsInitSpeed}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Connect Speed</RowTitle>
              <RowContent>{pusherStatsConnectSpeed}</RowContent>
            </SectionRow>
          </Row>
        </SectionBody>
      </Section>
    );
  };
  renderPusherSubscribeState = (channelName, channelInfo) => {
    const { t } = this.props;

    if (!channelInfo.subscribed) {
      return null;
    }

    return (
      <Row key={channelName}>
        <SectionRow>
          <ChannelName>{channelName}</ChannelName>
          <RowContent>
            {t('pusher_connected', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </RowContent>
        </SectionRow>
      </Row>
    );
  };
  renderPusherPanel = () => {
    const {
      t,
      id,
      clientId,
      pusherConnectionState,
      presenceUserSubscribeState,
      presenceClientSubscribeState,
    } = this.props;

    return (
      <Section key={'Pusher'}>
        <SectionTitle>
          Pusher
          <div>
            {t(`pusher_${pusherConnectionState}`, {
              ns: TranslationNamespace.SETTINGS,
            })}
          </div>
        </SectionTitle>
        <SectionBody>
          {presenceUserSubscribeState &&
            this.renderPusherSubscribeState(
              `presence-enc-user@${id}`,
              presenceUserSubscribeState
            )}
          {presenceClientSubscribeState &&
            this.renderPusherSubscribeState(
              `presence-enc-client@${clientId}`,
              presenceClientSubscribeState
            )}
        </SectionBody>
      </Section>
    );
  };
  renderIdInformation = () => {
    const {
      clientId,
      sessionId,
      fpIdOSS,
      fpIdPro,
      missingFpProRemoteConfigReason,
    } = this.props;
    return (
      <Section key={'ID Information'}>
        <SectionTitle>ID Information</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Client ID</RowTitle>
              <RowContent>{clientId}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Session ID</RowTitle>
              <RowContent>{sessionId}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Fingerprint ID (OSS)</RowTitle>
              <RowContent>{fpIdOSS}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Fingerprint ID (Pro)</RowTitle>
              <RowContent>
                {fpIdPro ? fpIdPro : missingFpProRemoteConfigReason}
              </RowContent>
            </SectionRow>
          </Row>
        </SectionBody>
      </Section>
    );
  };
  renderNtp = () => {
    const { clientTimestamp, serverTimestamp } = this.props;
    const offset =
      clientTimestamp == null || serverTimestamp == null
        ? null
        : Math.floor((serverTimestamp - clientTimestamp) / 1000);
    return (
      <Section key={'NTP'}>
        <SectionTitle>{`NTP ${
          offset == null ? '' : `(Offset: ${offset}s)`
        }`}</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Client</RowTitle>
              <RowContent>{clientTimestamp}</RowContent>
            </SectionRow>
          </Row>
          <Row>
            <SectionRow>
              <RowTitle>Server</RowTitle>
              <RowContent>{serverTimestamp}</RowContent>
            </SectionRow>
          </Row>
        </SectionBody>
      </Section>
    );
  };
  getAbPriorityName = ({ priority }) => {
    switch (priority) {
      case CONFIG_PRIORITY_CONFIGURE:
        return 'configure.js';
      case CONFIG_PRIORITY_PRESENCE_CLIENT:
        return 'client';
      case CONFIG_PRIORITY_PRESENCE_USER:
        return 'user';
      default:
        return '';
    }
  };
  renderTokensPanel = () => {
    const { abData, jwtData, refreshTokenJwtData } = this.props;
    const { shouldShowExtraInformation, accordions } = this.state;
    const tokens = {
      ...{ accessToken: jwtData },
      ...{ refreshToken: refreshTokenJwtData },
    };

    return (
      <Section key={'Tokens'}>
        <SectionTitle>Tokens</SectionTitle>
        <SectionBody>
          {/* access token, refresh token */}
          {Object.entries(tokens).map(([key, value]) => {
            if (isEmpty(value)) {
              return null;
            } else {
              const filteredValue = pick(value, [
                'scopes',
                'exp',
                'nbf',
                'iat',
              ]);
              const text = JSON.stringify(filteredValue, null, 2);
              const accordionId = `token-${key}`;
              const isActive = accordions[accordionId];

              return (
                <AccordionRow key={key}>
                  <Accordion
                    activeKey={isActive ? accordionId : null}
                    key={accordionId}
                    as={AccordionSectionRow}
                  >
                    <Toggle
                      eventKey={accordionId}
                      as={AccordionRowTitle}
                      isActive={isActive}
                      onClick={() => {
                        this.setState({
                          accordions: {
                            ...accordions,
                            [accordionId]: !isActive,
                          },
                        });
                      }}
                    >
                      <AccordionContent>{startCase(key)}</AccordionContent>
                      <AccordionDropDownIcon
                        src={DropDownIcon}
                        isActive={isActive}
                      />
                    </Toggle>
                    <Collapse eventKey={accordionId} as={AccordionRowContent}>
                      <CodeContent>{text}</CodeContent>
                    </Collapse>
                  </Accordion>
                </AccordionRow>
              );
            }
          })}

          {/* ab token */}
          {shouldShowExtraInformation
            ? Object.keys(abData).map(priority => {
                if (!abData?.[priority]?.experiments) {
                  return null;
                }
                const experimentObject = abData[priority].experiments;

                return Object.keys(experimentObject).map(experimentName => {
                  const variableObject =
                    experimentObject[experimentName]?.variables ?? {};
                  const variation =
                    experimentObject[experimentName]?.variation ?? '';

                  return (
                    <>
                      <Row>
                        <SectionRow>
                          <RowTitle>
                            {`AB Token: ${this.getAbPriorityName({ priority })}: ${experimentName}`}
                          </RowTitle>
                          <RowContent>{variation}</RowContent>
                        </SectionRow>
                      </Row>
                      {variableObject
                        ? Object.keys(variableObject).map(variableName => (
                            <Row key={variableName}>
                              <SectionRow>
                                <RowTitle>{variableName}</RowTitle>
                                <RowContent>
                                  {`${variableObject[variableName]}`}
                                </RowContent>
                              </SectionRow>
                            </Row>
                          ))
                        : null}
                    </>
                  );
                });
              })
            : null}
        </SectionBody>
      </Section>
    );
  };
  renderMobileConsoleToggle = () => {
    const { shouldShowExtraInformation } = this.state;
    if (!shouldShowExtraInformation) return null;

    const { isMobileConsoleEnabled } = this.state;

    return (
      <Section key="MobileConsole">
        <SectionBody>
          <Row key="MobileConsole">
            <SectionRow>
              <RowTitle>Mobile Console</RowTitle>
              <RowContent>
                <ToggleSwitch
                  active={isMobileConsoleEnabled}
                  onToggle={() => {
                    const isEnabled = !this.state.isMobileConsoleEnabled;

                    this.setState({
                      isMobileConsoleEnabled: isEnabled,
                    });

                    try {
                      // persist the state to localStorage for next page reload
                      localStorage.setItem(
                        '__isMobileConsoleEnabled',
                        isEnabled
                      );
                    } catch (_) {
                      //
                    }

                    if (isEnabled) {
                      initEruda();
                    } else {
                      destroyEruda();
                    }
                  }}
                />
              </RowContent>
            </SectionRow>
          </Row>
        </SectionBody>
      </Section>
    );
  };
  renderFcmTokenPanel = () => {
    const { notificationPermission, fcmToken } = this.props;

    return (
      <Section key={'FcmToken'}>
        <SectionTitle>FCM</SectionTitle>
        <SectionBody>
          <Row>
            <SectionRow>
              <RowTitle>Permission</RowTitle>
              <RowContent>{notificationPermission}</RowContent>
            </SectionRow>
          </Row>
          <TokenRow>
            <SectionRow>
              <TokenRowTitle>Token</TokenRowTitle>
              <TokenRowContent>{fcmToken}</TokenRowContent>
            </SectionRow>
          </TokenRow>
        </SectionBody>
      </Section>
    );
  };
  renderConfigurationGroups = () => {
    const {
      configurationGroups,
      remoteConfigMerged,
      currentConfigIds,
      currentConfigKey,
    } = this.props;
    const { shouldShowExtraInformation, accordions } = this.state;
    if (!shouldShowExtraInformation || !configurationGroups) {
      return null;
    }
    const currentConfigIdsAccordionId = 'current-config-ids';
    const isCurrentConfigIdsActive = !!accordions[currentConfigIdsAccordionId];
    return (
      <Section>
        <SectionTitle>Configuration Groups</SectionTitle>
        <SectionBody>
          {Object.entries(configurationGroups).map(([priority, value]) => {
            const text = JSON.stringify(value, null, 2);
            const accordionId = `configuration-groups-${priority}`;
            const isActive = !!accordions[accordionId];
            return (
              <AccordionRow key={priority}>
                <Accordion
                  activeKey={isActive ? accordionId : null}
                  key={accordionId}
                  as={AccordionSectionRow}
                >
                  <Toggle
                    eventKey={accordionId}
                    as={AccordionRowTitle}
                    isActive={isActive}
                    onClick={() => {
                      this.setState({
                        accordions: {
                          ...accordions,
                          [accordionId]: !isActive,
                        },
                      });
                    }}
                  >
                    <AccordionContent>
                      {priority} (Merged: {`${remoteConfigMerged?.[priority]}`})
                    </AccordionContent>
                    <AccordionDropDownIcon
                      src={DropDownIcon}
                      isActive={isActive}
                    />
                  </Toggle>
                  <Collapse eventKey={accordionId} as={AccordionRowContent}>
                    <CodeContent>{text}</CodeContent>
                  </Collapse>
                </Accordion>
              </AccordionRow>
            );
          })}
          {!currentConfigIds ? null : (
            <AccordionRow>
              <Accordion
                activeKey={
                  isCurrentConfigIdsActive ? currentConfigIdsAccordionId : null
                }
                as={AccordionSectionRow}
              >
                <Toggle
                  eventKey={currentConfigIdsAccordionId}
                  as={AccordionRowTitle}
                  isActive={isCurrentConfigIdsActive}
                  onClick={() => {
                    this.setState({
                      accordions: {
                        ...accordions,
                        [currentConfigIdsAccordionId]:
                          !isCurrentConfigIdsActive,
                      },
                    });
                  }}
                >
                  <AccordionContent>
                    Current Config Ids (Group: {currentConfigKey})
                  </AccordionContent>
                  <AccordionDropDownIcon
                    src={DropDownIcon}
                    isActive={isCurrentConfigIdsActive}
                  />
                </Toggle>
                <Collapse
                  eventKey={currentConfigIdsAccordionId}
                  as={AccordionRowContent}
                >
                  <CodeContent>
                    {JSON.stringify(currentConfigIds, null, 2)}
                  </CodeContent>
                </Collapse>
              </Accordion>
            </AccordionRow>
          )}
        </SectionBody>
      </Section>
    );
  };
  renderRemoteConfigSearch = () => {
    const { remoteConfig } = this.props;
    const { isRemoteConfigSearching, shouldShowExtraInformation } = this.state;
    if (!shouldShowExtraInformation || !remoteConfig) {
      return null;
    }
    return (
      <Section>
        <SectionTitle>Search Remote Config</SectionTitle>
        <SectionBody>
          <InputRow>
            <InputComponent
              theme={InputTheme.DARK}
              placeholderI18nKey="tabbar_search"
              placeholderI18nOption={{
                ns: TranslationNamespace.GENERAL,
              }}
              onChange={event => {
                this.setState({
                  isRemoteConfigSearching: true,
                });
                this.handleRemoteConfigSearch(event);
              }}
              trailingComponent={
                isRemoteConfigSearching ? (
                  <LoadingWrapper>
                    <Loading size={24} type={LoadingType.DARK} />
                  </LoadingWrapper>
                ) : (
                  <InputIcon
                    src={SearchIconSource}
                    alt="search"
                    width="20"
                    height="20"
                  />
                )
              }
              isEnabled
              isControlledComponent={false}
            />
          </InputRow>
        </SectionBody>
      </Section>
    );
  };
  renderConfigurationData = () => {
    const { configurationData, currentConfigIds } = this.props;
    const { shouldShowExtraInformation, accordions, remoteConfigKeyword } =
      this.state;
    if (!shouldShowExtraInformation || !configurationData) {
      return null;
    }
    return (
      <Section>
        <SectionTitle>Configuration Data</SectionTitle>
        <SectionBody>
          {currentConfigIds.map(key => {
            const value = configurationData[key];
            const text = this.getRemoteConfigText({
              data: value,
            });
            if ('{}' === text) return null;
            const accordionId = key;
            const isActive = !!accordions[accordionId];
            return (
              <AccordionRow key={key}>
                <Accordion
                  activeKey={isActive ? accordionId : null}
                  key={accordionId}
                  as={AccordionSectionRow}
                >
                  <Toggle
                    eventKey={accordionId}
                    as={AccordionRowTitle}
                    isActive={isActive}
                    onClick={() => {
                      this.setState({
                        accordions: {
                          ...accordions,
                          [accordionId]: !isActive,
                        },
                      });
                    }}
                  >
                    <AccordionContent>{key}</AccordionContent>
                    <AccordionDropDownIcon
                      src={DropDownIcon}
                      isActive={isActive}
                    />
                  </Toggle>
                  <Collapse eventKey={accordionId} as={AccordionRowContent}>
                    <CodeContent>
                      <HighlightSubstring substring={remoteConfigKeyword}>
                        {text}
                      </HighlightSubstring>
                    </CodeContent>
                  </Collapse>
                </Accordion>
              </AccordionRow>
            );
          })}
          {configurationData?.webview && (
            <AccordionRow>
              <Accordion
                activeKey={accordions['webview'] ? 'webview' : null}
                as={AccordionSectionRow}
              >
                <Toggle
                  eventKey="webview"
                  as={AccordionRowTitle}
                  isActive={accordions['webview']}
                  onClick={() => {
                    this.setState({
                      accordions: {
                        ...accordions,
                        webview: !accordions['webview'],
                      },
                    });
                  }}
                >
                  webview
                  <AccordionDropDownIcon
                    src={DropDownIcon}
                    isActive={accordions['webview']}
                  />
                </Toggle>
                <Collapse eventKey="webview" as={AccordionRowContent}>
                  <CodeContent>
                    <HighlightSubstring substring={remoteConfigKeyword}>
                      {this.getRemoteConfigText({
                        data: configurationData.webview,
                      })}
                    </HighlightSubstring>
                  </CodeContent>
                </Collapse>
              </Accordion>
            </AccordionRow>
          )}
          {configurationData?.mdm && (
            <AccordionRow>
              <Accordion
                activeKey={accordions['mdm'] ? 'mdm' : null}
                as={AccordionSectionRow}
              >
                <Toggle
                  eventKey="mdm"
                  as={AccordionRowTitle}
                  isActive={accordions['mdm']}
                  onClick={() => {
                    this.setState({
                      accordions: {
                        ...accordions,
                        mdm: !accordions['mdm'],
                      },
                    });
                  }}
                >
                  mdm
                  <AccordionDropDownIcon
                    src={DropDownIcon}
                    isActive={accordions['mdm']}
                  />
                </Toggle>
                <Collapse eventKey="mdm" as={AccordionRowContent}>
                  <CodeContent>
                    <HighlightSubstring substring={remoteConfigKeyword}>
                      {this.getRemoteConfigText({
                        data: configurationData.mdm,
                      })}
                    </HighlightSubstring>
                  </CodeContent>
                </Collapse>
              </Accordion>
            </AccordionRow>
          )}
        </SectionBody>
      </Section>
    );
  };
  /**
   * Get the formatted text of the data.
   * @param {object} data - The data to be processed.
   * @returns {string} The formatted data as a string.
   */
  getRemoteConfigText = ({ data = {} } = {}) => {
    const { remoteConfigKeyword } = this.state;
    let result = data;
    if (remoteConfigKeyword) {
      result = Object.entries(result)
        .filter(entry => {
          const isIncludeKeyword = entry.some(item =>
            item.toLowerCase().includes(remoteConfigKeyword.toLowerCase())
          );
          return isIncludeKeyword;
        })
        .reduce((accumulated, item) => {
          const [key, value] = item;
          accumulated[key] = value;
          return accumulated;
        }, {});
    }
    return JSON.stringify(result, null, 2);
  };
  renderRemoteConfig = () => {
    const { remoteConfig } = this.props;
    const { shouldShowExtraInformation, accordions, remoteConfigKeyword } =
      this.state;
    if (!shouldShowExtraInformation || !remoteConfig) {
      return null;
    }
    const text = this.getRemoteConfigText({ data: remoteConfig.toJS() });
    const isActive = !!accordions[REMOTE_CONFIG_ACCORDION_ID];
    return (
      <Section>
        <SectionTitle>Remote Config</SectionTitle>
        <SectionBody>
          <AccordionRow>
            <Accordion
              activeKey={isActive ? REMOTE_CONFIG_ACCORDION_ID : null}
              as={AccordionSectionRow}
            >
              <Toggle
                eventKey={REMOTE_CONFIG_ACCORDION_ID}
                as={AccordionRowTitle}
                isActive={isActive}
                onClick={() => {
                  this.setState({
                    accordions: {
                      ...accordions,
                      [REMOTE_CONFIG_ACCORDION_ID]: !isActive,
                    },
                  });
                }}
              >
                <AccordionContent>Remote Config</AccordionContent>
                <AccordionDropDownIcon src={DropDownIcon} isActive={isActive} />
              </Toggle>
              <Collapse
                eventKey={REMOTE_CONFIG_ACCORDION_ID}
                as={AccordionRowContent}
              >
                <CodeContent>
                  <HighlightSubstring substring={remoteConfigKeyword}>
                    {text}
                  </HighlightSubstring>
                </CodeContent>
              </Collapse>
            </Accordion>
          </AccordionRow>
        </SectionBody>
      </Section>
    );
  };
  renderDataAttributeToggle = () => {
    const { isElementIdDebugTrackingEnabled, updateDebugTracking } = this.props;
    const { shouldShowExtraInformation } = this.state;
    if (!shouldShowExtraInformation) return null;
    const attributes = {
      ['data-element_id']: {
        key: 'elementId',
        isEnabled: isElementIdDebugTrackingEnabled,
      },
    };
    return (
      <Section key={'DataAttribute'}>
        <SectionTitle>Data Attribute</SectionTitle>
        <SectionBody>
          {Object.entries(attributes).map(([attribute, { key, isEnabled }]) => (
            <Row key={attribute}>
              <SectionRow>
                <RowTitle>{attribute}</RowTitle>
                <RowContent>
                  <ToggleSwitch
                    active={isEnabled}
                    onToggle={() =>
                      updateDebugTracking({
                        key,
                        value: !isEnabled,
                      })
                    }
                  />
                </RowContent>
              </SectionRow>
            </Row>
          ))}
        </SectionBody>
      </Section>
    );
  };
  render() {
    const { t, goBack, isSpeedtesting, enableEngagementDebugger, xTrack } =
      this.props;
    const { accordions } = this.state;
    const isActive = !!accordions['userAgent'];

    return (
      <StyledDebugPanel>
        <Header>
          <BackButton onClick={goBack}>
            <img src={BackIconSource} alt="go back" />
          </BackButton>
          <Title
            onClick={() => {
              this.titleClickTimes += 1;
              if (this.titleClickTimes >= 10) {
                this.setState({
                  shouldShowExtraInformation: true,
                });
                enableEngagementDebugger();
              }
            }}
          >
            Debug
          </Title>
          <ActionButton
            data-element_id={ButtonId.All.ButtonCheck}
            onClick={() => this.handleCheckButtonOnClick()}
            disabled={isSpeedtesting}
          >
            {t('general_check', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </ActionButton>
        </Header>
        <Body>
          <Section>
            <SectionBody>
              <AccordionRow key={'UserAgent'}>
                <Accordion
                  activeKey={isActive ? 'UserAgent' : null}
                  key={'UserAgent'}
                  as={AccordionSectionRow}
                >
                  <Toggle
                    eventKey={'UserAgent'}
                    as={AccordionRowTitle}
                    isActive={isActive}
                    onClick={() => {
                      this.setState({
                        accordions: {
                          ...accordions,
                          userAgent: !isActive,
                        },
                      });
                    }}
                  >
                    <AccordionContent>{ua}</AccordionContent>
                    <AccordionDropDownIcon
                      src={DropDownIcon}
                      isActive={isActive}
                    />
                  </Toggle>
                  <Collapse eventKey={'UserAgent'} as={AccordionRowContent}>
                    <CodeContent>
                      <div>{`Browser: ${JSON.stringify(uaBrowser)}`}</div>
                      <div>{`Device: ${JSON.stringify(uaDevice)}`}</div>
                      <div>{`OS: ${JSON.stringify(uaOs)}`}</div>
                      <div>{`Engine: ${JSON.stringify(uaEngine)}`}</div>
                    </CodeContent>
                  </Collapse>
                </Accordion>
              </AccordionRow>
              <Row>
                <SectionRow>
                  <RowTitle>X-Track</RowTitle>
                  <RowContent>{xTrack}</RowContent>
                </SectionRow>
              </Row>
            </SectionBody>
          </Section>
          {this.renderWebViewWebAppConfig()}
          {this.renderMdmConfig()}
          {isOnWebviewApp &&
            isOnAndroid &&
            this.renderCheckWebviewSettingsButton()}
          {this.renderDrmPanel()}
          {this.renderPusherStatsPanel()}
          {this.renderPusherPanel()}
          {this.renderIdInformation()}
          {this.renderFcmTokenPanel()}
          {this.renderNtp()}
          {this.renderTokensPanel()}
          {this.renderMobileConsoleToggle()}
          {this.renderSpeedtestPanel()}
          {this.renderDataAttributeToggle()}
          {this.renderConfigurationGroups()}
          {this.renderRemoteConfigSearch()}
          {this.renderConfigurationData()}
          {this.renderRemoteConfig()}
        </Body>
      </StyledDebugPanel>
    );
  }
}

DebugPanel.propTypes = {
  t: PropTypes.func.isRequired,
  goBack: PropTypes.func,
  checkNetworkSpeed: PropTypes.func,
  recheckDrmCapability: PropTypes.func,
  trackSpeedtestEvent: PropTypes.func,
  updateDebugTracking: PropTypes.func,
  handleRefreshToken: PropTypes.func,
  postMessageToApp: PropTypes.func,
  isSpeedtesting: PropTypes.bool,
  isSpeedtested: PropTypes.bool,
  dlProgress: PropTypes.number,
  ulProgress: PropTypes.number,
  pingStatus: PropTypes.string,
  jitterStatus: PropTypes.string,
  dlStatus: PropTypes.string,
  ulStatus: PropTypes.string,
  speedtestUnixTimestamp: PropTypes.number,
  drmTestAtUnixTimestamp: PropTypes.number,
  drmTestStatus: PropTypes.string,
  pusherStatsInitSpeed: PropTypes.string,
  pusherStatsConnectSpeed: PropTypes.string,
  messageId: PropTypes.string,
  abData: PropTypes.object,
  notificationPermission: PropTypes.string,
  fcmToken: PropTypes.string,
  clientTimestamp: PropTypes.number,
  serverTimestamp: PropTypes.number,
  id: PropTypes.string,
  clientId: PropTypes.string,
  sessionId: PropTypes.string,
  configurationData: PropTypes.object,
  configurationGroups: PropTypes.object,
  remoteConfigMerged: PropTypes.object,
  currentConfigIds: PropTypes.array,
  currentConfigKey: PropTypes.string,
  remoteConfig: PropTypes.object,
  speedtestServersUrl: PropTypes.string,
  isElementIdDebugTrackingEnabled: PropTypes.bool,
  pusherConnectionState: PropTypes.string,
  presenceUserSubscribeState: PropTypes.object,
  presenceClientSubscribeState: PropTypes.object,
  refreshToken: PropTypes.string,
  jwtData: PropTypes.object,
  refreshTokenJwtData: PropTypes.object,
  fpIdOSS: PropTypes.string,
  fpIdPro: PropTypes.string,
  xTrack: PropTypes.string,
  missingFpProRemoteConfigReason: PropTypes.string,
  getFpIdPro: PropTypes.func,
  enableEngagementDebugger: PropTypes.func,
};

DebugPanel.defaultProps = {
  goBack: NULL_FUNCTION,
  checkNetworkSpeed: NULL_FUNCTION,
  recheckDrmCapability: NULL_FUNCTION,
  trackSpeedtestEvent: NULL_FUNCTION,
  updateDebugTracking: NULL_FUNCTION,
  handleRefreshToken: NULL_FUNCTION,
  postMessageToApp: NULL_FUNCTION,
  isSpeedtesting: false,
  isSpeedtested: false,
  dlProgress: null,
  ulProgress: null,
  pingStatus: null,
  jitterStatus: null,
  dlStatus: null,
  ulStatus: null,
  speedtestUnixTimestamp: null,
  drmTestAtUnixTimestamp: null,
  drmTestStatus: null,
  pusherStatsInitSpeed: null,
  pusherStatsConnectSpeed: null,
  messageId: null,
  abData: {},
  notificationPermission: '',
  fcmToken: '',
  clientTimestamp: null,
  serverTimestamp: null,
  id: null,
  clientId: null,
  sessionId: null,
  configurationData: null,
  configurationGroups: null,
  remoteConfigMerged: null,
  currentConfigIds: null,
  currentConfigKey: null,
  remoteConfig: null,
  speedtestServersUrl: '',
  isElementIdDebugTrackingEnabled: false,
  pusherConnectionState: 'disconnected',
  presenceUserSubscribeState: {},
  presenceClientSubscribeState: {},
  refreshToken: '',
  jwtData: {},
  refreshTokenJwtData: {},
  fpIdOSS: '',
  fpIdPro: '',
  xTrack: '',
  missingFpProRemoteConfigReason: '',
  getFpIdPro: NULL_FUNCTION,
  enableEngagementDebugger: NULL_FUNCTION,
};

const StyledDebugPanel = styled(StyledModal)``;

const Body = styled(SettingModalBody)`
  padding-top: 16px;
  padding-bottom: 100px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const AccordionContent = styled.div`
  padding: 10px 8px 10px 0;
  white-space: pre-wrap;
`;

const TokenRow = styled(Row)`
  height: 100%;
  padding: 10px 0;
`;

const TokenRowTitle = styled(RowTitle)`
  flex-shrink: 0;
`;

const TokenRowContent = styled(RowContent)`
  flex-shrink: 1;
  word-break: break-all;
`;

const CodeContent = styled.div`
  margin-bottom: 16px;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const InputRow = styled(Row)`
  padding-top: 8px;
  padding-bottom: 8px;
  height: auto;
`;

const LoadingWrapper = styled.div``;

const InputIcon = styled.img`
  flex: none;
`;

const ChannelName = styled.div`
  display: flex;
  align-items: center;
  flex: auto;
  margin-right: 12px;
  font-size: 14px;
  word-break: break-all;
`;

export default withTranslation()(DebugPanel);
